import React, { useEffect, useState } from 'react';
import Button from '../UI/Button';
import Card from '../UI/Card';
import {
  ReferralSettings,
  AgentReferralSettings,
} from '../../api/services/platformSettings.service';

interface ReferralTabProps {
  value: ReferralSettings;
  agentValue: AgentReferralSettings;
  onSave: (
    referral: ReferralSettings,
    agentReferral: AgentReferralSettings
  ) => Promise<void>;
  saving: boolean;
}

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

/* Convert ISO string → <input type="datetime-local"> value */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

const ReferralTab: React.FC<ReferralTabProps> = ({
  value,
  agentValue,
  onSave,
  saving,
}) => {
  const [form, setForm] = useState<ReferralSettings>(value);
  const [agentForm, setAgentForm] =
    useState<AgentReferralSettings>(agentValue);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm(value);
    setDirty(false);
  }, [value]);

  useEffect(() => {
    setAgentForm(agentValue);
    setDirty(false);
  }, [agentValue]);

  const set = <K extends keyof ReferralSettings>(
    k: K,
    v: ReferralSettings[K]
  ) => {
    setForm((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  const setAgent = <K extends keyof AgentReferralSettings>(
    k: K,
    v: AgentReferralSettings[K]
  ) => {
    setAgentForm((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  const handleSave = () => onSave(form, agentForm);

  return (
    <div className="space-y-4">
      {/* ============== USER REFERRAL ============== */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-900 mb-4">
          User Referral Settings
        </h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">
                User Referral Program
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Enable or disable the standard user referral rewards.
              </p>
            </div>
            <button
              onClick={() => set('enabled', !form.enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.enabled ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Reward Amount (ETB)</label>
              <input
                type="number"
                min={0}
                value={form.rewardAmount}
                onChange={(e) =>
                  set('rewardAmount', Number(e.target.value) || 0)
                }
                className={inputClass}
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Amount credited to a referrer per successful signup.
              </p>
            </div>
            <div>
              <label className={labelClass}>Minimum Deposit to Count</label>
              <input
                type="number"
                min={0}
                value={form.minDepositToCount}
                onChange={(e) =>
                  set('minDepositToCount', Number(e.target.value) || 0)
                }
                className={inputClass}
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Referral only counts after the referred user deposits at
                least this much.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* ============== AGENT REFERRAL ============== */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-900 mb-4">
          Agent Referral Settings
        </h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Agent Referral Program
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Master switch for all agents. When off, no agent earns
                rewards regardless of their individual setting.
              </p>
            </div>
            <button
              onClick={() => setAgent('enabled', !agentForm.enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                agentForm.enabled ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  agentForm.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Default Reward Amount (ETB)
              </label>
              <input
                type="number"
                min={0}
                value={agentForm.defaultRewardAmount}
                onChange={(e) =>
                  setAgent(
                    'defaultRewardAmount',
                    Number(e.target.value) || 0
                  )
                }
                className={inputClass}
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Used for any agent without a custom reward. Per-agent amounts
                are set on the Referrals page.
              </p>
            </div>
            <div>
              <label className={labelClass}>
                Minimum First Deposit (ETB)
              </label>
              <input
                type="number"
                min={0}
                value={agentForm.minDepositAmount ?? 0}
                onChange={(e) =>
                  setAgent(
                    'minDepositAmount',
                    Number(e.target.value) || 0
                  )
                }
                className={inputClass}
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Agent is rewarded only when the referred user's first
                successful deposit is at least this much. Set 0 to reward
                any first deposit.
              </p>
            </div>
            <div>
              <label className={labelClass}>
                Reward Window (days)
              </label>
              <input
                type="number"
                min={0}
                value={agentForm.rewardWindowDays ?? 0}
                onChange={(e) =>
                  setAgent(
                    'rewardWindowDays',
                    Number(e.target.value) || 0
                  )
                }
                className={inputClass}
              />
              <p className="text-[10px] text-gray-500 mt-1">
                How long after a referred user joins the agent can still
                earn. 0 = no time limit.
              </p>
            </div>
            {/* ⭐ NEW — program start date cutoff */}
            <div>
              <label className={labelClass}>
                Agent Program Start Date
              </label>
              <input
                type="datetime-local"
                value={toLocalInput(agentForm.startedAt)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) {
                    setAgent('startedAt', null);
                  } else {
                    const d = new Date(v);
                    setAgent(
                      'startedAt',
                      isNaN(d.getTime()) ? null : d.toISOString()
                    );
                  }
                }}
                className={inputClass}
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Referrals created before this moment are never eligible
                for agent rewards. Their regular referral history is
                unaffected. Leave empty to reward any referral.
              </p>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-purple-50 border border-purple-100">
            <p className="text-[11px] text-purple-800">
              Agents are promoted from the{' '}
              <span className="font-semibold">Referrals</span> page. That page
              also lets you set a per-agent reward that overrides the default
              here.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {saving ? 'Saving...' : 'Save Referral Settings'}
        </Button>
      </div>
    </div>
  );
};

export default ReferralTab;